import React, { useContext, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import {
  FormInputTooltip,
  FormInputTooltipBubble
} from 'src/components/atoms/FormInputTooltipBubble'
import moment from 'moment'
import { useIntl, Link } from 'gatsby-plugin-intl'
import useValidateSchema from 'src/hooks/useValidateSchema'
import { compose } from 'recompose'
import { breakpoint } from 'src/theme/grid'
import { scrollToRef } from 'src/utils/scroll'
import HeaderLoggedIn, {
  HeaderGroup
} from 'src/components/header/HeaderLoggedIn'
import { Page } from 'src/components/Wrappers'
import BreadCrumbs from 'src/components/BreadCrumbs'
import { MENU_ITEMS } from 'src/features/dashboard/duck/consts'
import LocationInput from 'src/components/filters/LocationInput'
import routes from 'src/utils/routes'
import { connectDictionaries } from 'src/ducks/connectors'
import Checkbox from 'src/components/atoms/CheckboxInput'
import Select from 'src/components/filters/SelectInput'
import TextInput from 'src/components/atoms/TextInput'
import TextInputMasked from 'src/components/atoms/TextInputMasked'
import DatePicker from 'src/components/atoms/DatePicker'
import Alert from 'src/components/Alert'
import {
  BOOLEAN,
  LOCATION_TYPES,
  API_STATES,
  EXCHANGE_API_URL_PREFIXES
} from 'src/ducks/consts'
import { EURO_OPTIONS } from 'src/features/stocks/ducks/consts'
import {
  OFFER_TYPES,
  OFFER_TYPES_OPTIONS,
  OFFER_VISIBILITY_TYPES,
  OFFER_VISIBILITY_TYPES_OPTIONS
} from 'src/features/offers/duck/consts'
import IconWithTooltip from 'src/components/IconWithTooltip'
import NewForeignLocationsAddedAlert from 'src/features/offers/components/NewForeignLocationsAddedAlert'
import PermissionsContext from 'src/utils/permissions'
import PrivateOfferCreateOptions from 'src/features/offers/components/PrivateOfferCreateOptions'
import { useSelector } from 'react-redux'
import { canAddOffer } from 'src/features/offers/utils/exchangePermissions'
import CreateOfferMenu from './components/CreateOfferMenu'
import {
  Content,
  FormSection,
  FormInnerWrapper,
  FormSectionSpacer,
  FormActionBar,
  InputsRowWrapper,
  InputWrapper,
  FormSubsectionSpacer,
  PlaceInputWrapper,
  DateInputWrapper,
  TimeInputWrapper,
  DictionaryInputWrapper,
  WeightInputWrapper,
  PriceInputWrapper,
  CargoTypeInputWrapper,
  Padding,
  TemplateNameInputWrapper,
  TemplatesButton,
  ActionGroup,
  HideOnMobile,
  RadioWrapper,
  WarningWrapper,
  AuctionTimeInputWrapper,
  AuctionNotesWrapper
} from './components/atoms'
import Drafts from './components/Drafts'
import { FormNavigationHeader } from './components/typography'
import connect from './duck/connectors'
import CheckboxSection from './components/CheckboxSection'
import { validateOfferImport, validateOfferImportSchema } from './duck/schema'
import { getSharedGeneralCargoFields } from './utils/generalCargoPrefill'

const OfferFormImport = ({
  className,
  offerFormImport,
  setOfferFormImportValue,
  setOfferFormGeneralValue,
  clearImportOfferForm,
  fetchLocations,
  pickupDictionary,
  unloadingDictionary,
  dictionaries,
  returnDictionary,
  destinationDictionary,
  getImportMyCompanyLastOffers,
  getImportOfferTemplates,
  lastOffersImport,
  offerTemplatesImport,
  fillFormUsingTemplateImport,
  removeTemplateImport,

  createImportOffer,
  createBulkImportOffers,
  createAndDuplicateImportOffer,
  createAndStartNewImportOffer
}) => {
  const intl = useIntl()
  const templateRef = useRef(null)
  const formContentRef = useRef(null)
  const permissions = useContext(PermissionsContext)
  const currentUser = useSelector(state => state.account.currentUser)
  const canCreatePublicOffer = canAddOffer(
    EXCHANGE_API_URL_PREFIXES.IMPORT,
    permissions,
    OFFER_VISIBILITY_TYPES.PUBLIC
  )
  const canCreatePrivateOffer = canAddOffer(
    EXCHANGE_API_URL_PREFIXES.IMPORT,
    permissions,
    OFFER_VISIBILITY_TYPES.PRIVATE
  )
  const visibilityOptions = OFFER_VISIBILITY_TYPES_OPTIONS.map(option => ({
    ...option,
    text: intl.formatMessage({ id: option.text })
  }))
  const availableVisibilityOptions = visibilityOptions.filter(option =>
    option.value === OFFER_VISIBILITY_TYPES.PUBLIC
      ? canCreatePublicOffer
      : canCreatePrivateOffer
  )
  const hasAvailableVisibilityOptions = availableVisibilityOptions.length > 0
  const shouldShowVisibilitySelector = availableVisibilityOptions.length > 1

  const [templatesOpened, setTemplatesOpened] = useState(false)
  const valid = useValidateSchema(
    offerFormImport.toJS(),
    validateOfferImportSchema
  )

  useEffect(() => {
    let defaultVisibility = null

    if (currentUser?.isPrivateOfferDefault && canCreatePrivateOffer) {
      defaultVisibility = OFFER_VISIBILITY_TYPES.PRIVATE
    } else if (canCreatePublicOffer) {
      defaultVisibility = OFFER_VISIBILITY_TYPES.PUBLIC
    } else if (canCreatePrivateOffer) {
      defaultVisibility = OFFER_VISIBILITY_TYPES.PRIVATE
    }

    setOfferFormImportValue('visibility', defaultVisibility)
  }, [
    canCreatePrivateOffer,
    canCreatePublicOffer,
    currentUser?.isPrivateOfferDefault,
    setOfferFormImportValue
  ])

  useEffect(() => {
    if (offerFormImport.visibility !== OFFER_VISIBILITY_TYPES.PRIVATE) {
      setOfferFormImportValue('rooms', null)
      setOfferFormImportValue('goPublicAt', null)
    }
  }, [offerFormImport.visibility, setOfferFormImportValue])

  useEffect(() => {
    getImportMyCompanyLastOffers()
    getImportOfferTemplates()
    return clearImportOfferForm
  }, [])

  const isAuction = offerFormImport.type === OFFER_TYPES.AUCTION
  const shipownerLabel = dictionaries.shipowners.find(
    ({ value }) => value === offerFormImport.shipownerId
  )?.label
  const isAnyShipowner = ['Any', 'Kazdy armator'].includes(shipownerLabel)
  const copyToGeneralCargoForm = () =>
    setOfferFormGeneralValue(getSharedGeneralCargoFields(offerFormImport))

  return (
    <Page
      className={className}
      contentPadding={0}
      topBar={
        <HeaderLoggedIn>
          <HeaderGroup>
            <FormNavigationHeader>
              {intl.formatMessage({ id: 'forms.importFormTitle' })}
            </FormNavigationHeader>
          </HeaderGroup>
        </HeaderLoggedIn>
      }
    >
      <Content>
        <FormSection>
          <FormInnerWrapper ref={formContentRef}>
            <Padding>
              <BreadCrumbs
                links={[
                  {
                    name: intl.formatMessage({ id: 'forms.dashboard' }),
                    url: `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.DASHBOARD}`
                  },
                  {
                    name: intl.formatMessage({
                      id: 'forms.exchangeImportList'
                    }),
                    url: `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_CARGO_IMPORT}`
                  },
                  {
                    name: intl.formatMessage({ id: 'forms.addOffer' }),
                    url: null
                  }
                ]}
              />
              <TemplatesButton onClick={() => setTemplatesOpened(true)}>
                {intl.formatMessage({ id: 'forms.copyOrUseTemplate' })}
              </TemplatesButton>
              <NewForeignLocationsAddedAlert />
              <FormSectionSpacer>
                <RadioWrapper>
                  <div>{intl.formatMessage({ id: 'forms.offerType' })}</div>
                  {OFFER_TYPES_OPTIONS.map(option => ({
                    ...option,
                    text: intl.formatMessage({ id: option.text })
                  })).map(opt => (
                    <Checkbox
                      key={opt.value}
                      name='type'
                      value={offerFormImport.type}
                      onChange={setOfferFormImportValue}
                      type='radio'
                      option={opt}
                    />
                  ))}
                </RadioWrapper>
              </FormSectionSpacer>
              {isAuction && (
                <>
                  <WarningWrapper>
                    <Alert
                      message={intl.formatMessage({
                        id: 'forms.auctionWarning'
                      })}
                      type='info'
                    />
                  </WarningWrapper>
                  <InputsRowWrapper>
                    <DateInputWrapper>
                      <DatePicker
                        label={intl.formatMessage({
                          id: 'forms.auctionEndDate'
                        })}
                        disablePast
                        required
                        name='auctionDeadlineDate'
                        placeholder='dd.mm.rrrr'
                        value={offerFormImport.auctionDeadlineDate}
                        onChange={setOfferFormImportValue}
                        maxDate={moment().add(6, 'days')}
                      />
                    </DateInputWrapper>
                    <AuctionTimeInputWrapper>
                      <TextInputMasked
                        mask='99:99'
                        label={intl.formatMessage({
                          id: 'forms.auctionEndTime'
                        })}
                        required
                        id='auctionDeadlineTime'
                        name='auctionDeadlineTime'
                        placeholder='gg:mm'
                        value={offerFormImport.auctionDeadlineTime}
                        onChange={setOfferFormImportValue}
                        validate={validateOfferImport}
                        type='tel'
                        calendarTime
                      />
                    </AuctionTimeInputWrapper>
                    <PriceInputWrapper>
                      <TextInput
                        label={intl.formatMessage({
                          id: 'forms.auctionBasePrice'
                        })}
                        id='price'
                        name='price'
                        required
                        placeholder=''
                        value={offerFormImport.price}
                        onChange={setOfferFormImportValue}
                        validate={validateOfferImport}
                        type='currency'
                        onCurrencyChange={setOfferFormImportValue}
                        currencyValue={offerFormImport.currency}
                      />
                    </PriceInputWrapper>
                    <Checkbox
                      name='isPriceVisible'
                      value={offerFormImport.isPriceVisible}
                      option={{
                        value: BOOLEAN.YES,
                        text: intl.formatMessage({
                          id: 'forms.isPriceVisible'
                        })
                      }}
                      onChange={name => {
                        setOfferFormImportValue(
                          name,
                          !offerFormImport.isPriceVisible
                        )
                      }}
                    />
                    <AuctionNotesWrapper>
                      <TextInput
                        label={intl.formatMessage({
                          id: 'forms.auctionNotes'
                        })}
                        id='notes'
                        name='notes'
                        multiline
                        rows={3}
                        placeholder={intl.formatMessage({
                          id: 'forms.auctionNotesPlaceholder'
                        })}
                        value={offerFormImport.notes}
                        onChange={setOfferFormImportValue}
                        validate={validateOfferImport}
                      />
                    </AuctionNotesWrapper>
                  </InputsRowWrapper>
                </>
              )}
              {shouldShowVisibilitySelector && (
                <>
                  <FormSectionSpacer>
                    <RadioWrapper>
                      <div>
                        {intl.formatMessage({ id: 'offersFilters.visibility' })}
                      </div>
                      {availableVisibilityOptions.map(opt => (
                        <React.Fragment key={opt.value}>
                          <Checkbox
                            name='visibility'
                            value={offerFormImport.visibility}
                            onChange={setOfferFormImportValue}
                            type='radio'
                            option={opt}
                          />
                          {opt.value === OFFER_VISIBILITY_TYPES.PRIVATE && (
                            <IconWithTooltip
                              title={intl.formatMessage({
                                id: 'forms.privateOfferCreateOptionsInfoMessage'
                              })}
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </RadioWrapper>
                  </FormSectionSpacer>
                </>
              )}
              {!hasAvailableVisibilityOptions && (
                <Alert
                  message={intl.formatMessage({
                    id: 'forms.offerVisibilityNoPermissions'
                  })}
                  type='warning'
                />
              )}
              {/* {JSON.stringify(offerFormExport)} */}
              {offerFormImport.visibility ===
                OFFER_VISIBILITY_TYPES.PRIVATE && (
                <PrivateOfferCreateOptions
                  exchangeType={EXCHANGE_API_URL_PREFIXES.IMPORT}
                  onGoPublicDateTimeChange={setOfferFormImportValue}
                  rooms={offerFormImport.rooms}
                  canCreatePublicOffer={canCreatePublicOffer}
                />
              )}
              <FormSectionSpacer>
                {intl.formatMessage({ id: 'forms.tracking' })}
                <IconWithTooltip
                  openTime={3000}
                  title={intl.formatMessage({
                    id: 'forms.trackingHelperForwarder'
                  })}
                />
              </FormSectionSpacer>
              <Checkbox
                name='doesRequireTracking'
                value={offerFormImport.doesRequireTracking}
                option={{
                  value: BOOLEAN.YES,
                  text: intl.formatMessage({
                    id: 'forms.doesRequireTrackingForwarder'
                  })
                }}
                onChange={name => {
                  setOfferFormImportValue(
                    name,
                    !offerFormImport.doesRequireTracking
                  )
                }}
              />
              <FormSectionSpacer>
                {intl.formatMessage({ id: 'forms.pickupSectionName' })}
              </FormSectionSpacer>
              <InputsRowWrapper>
                <PlaceInputWrapper>
                  <LocationInput
                    required
                    id='pickupLocationId'
                    label={intl.formatMessage({
                      id: 'forms.pickupPlaceInputLabel'
                    })}
                    locationName='pickupLocationId'
                    locationValue={offerFormImport.pickupLocationId}
                    locationPlaceholder={intl.formatMessage({
                      id: 'forms.pickupPlaceInputLabel'
                    })}
                    onChange={(name, value) =>
                      setOfferFormImportValue(name, value)
                    }
                    placeholder={intl.formatMessage({
                      id: 'forms.locationPlaceholder'
                    })}
                    locationTypes={LOCATION_TYPES.TERMINAL}
                    dictionary={pickupDictionary}
                    isLoading={
                      pickupDictionary.state === API_STATES.IN_PROGRESS
                    }
                    fetchLocations={fetchLocations}
                    searchHintText={intl.formatMessage({
                      id: 'offersFilters.searchHintText'
                    })}
                  />
                </PlaceInputWrapper>
                <DateInputWrapper>
                  <DatePicker
                    label={intl.formatMessage({
                      id: 'forms.pickupDateInputLabel'
                    })}
                    disablePast
                    required
                    name='pickupDate'
                    placeholder='dd.mm.rrrr'
                    value={offerFormImport.pickupDate}
                    onChange={setOfferFormImportValue}
                    maxDate={offerFormImport.unloadingDate}
                  />
                </DateInputWrapper>
                <TimeInputWrapper>
                  <TextInputMasked
                    mask='99:99'
                    label={intl.formatMessage({
                      id: 'forms.pickupTimeInputLabel'
                    })}
                    id='pickupTime'
                    name='pickupTime'
                    placeholder='gg:mm'
                    value={offerFormImport.pickupTime}
                    onChange={setOfferFormImportValue}
                    validate={validateOfferImport}
                    type='tel'
                    calendarTime
                  />
                </TimeInputWrapper>
              </InputsRowWrapper>
              <FormSectionSpacer>
                {intl.formatMessage({ id: 'forms.unloadingSectionName' })}
              </FormSectionSpacer>
              <InputsRowWrapper>
                <PlaceInputWrapper>
                  <LocationInput
                    required
                    id='unloadingLocationId'
                    label={intl.formatMessage({
                      id: 'forms.unloadingPlaceInputLabel'
                    })}
                    locationName='unloadingLocationId'
                    locationValue={offerFormImport.unloadingLocationId}
                    locationPlaceholder={intl.formatMessage({
                      id: 'forms.unloadingPlaceInputLabel'
                    })}
                    onChange={(name, value) =>
                      setOfferFormImportValue(name, value)
                    }
                    placeholder={intl.formatMessage({
                      id: 'forms.locationPlaceholder'
                    })}
                    locationTypes={LOCATION_TYPES.CITY}
                    dictionary={unloadingDictionary}
                    isLoading={
                      unloadingDictionary.state === API_STATES.IN_PROGRESS
                    }
                    fetchLocations={fetchLocations}
                    searchHintText={intl.formatMessage({
                      id: 'offersFilters.searchHintText'
                    })}
                  />
                </PlaceInputWrapper>
                <DateInputWrapper>
                  <DatePicker
                    label={intl.formatMessage({
                      id: 'forms.unloadingDateInputLabel'
                    })}
                    disablePast
                    required
                    name='unloadingDate'
                    placeholder='dd.mm.rrrr'
                    value={offerFormImport.unloadingDate}
                    onChange={setOfferFormImportValue}
                    minDate={offerFormImport.pickupDate}
                  />
                </DateInputWrapper>
                <TimeInputWrapper>
                  <TextInputMasked
                    mask='99:99'
                    label={intl.formatMessage({
                      id: 'forms.unloadingTimeInputLabel'
                    })}
                    id='unloadingTime'
                    name='unloadingTime'
                    placeholder='gg:mm'
                    value={offerFormImport.unloadingTime}
                    onChange={setOfferFormImportValue}
                    validate={validateOfferImport}
                    type='tel'
                    calendarTime
                  />
                </TimeInputWrapper>
              </InputsRowWrapper>
            </Padding>
            <CheckboxSection
              opened={offerFormImport.returnLocationIdOpened}
              setOpened={opened =>
                setOfferFormImportValue('returnLocationIdOpened', opened)
              }
              label={intl.formatMessage({
                id: 'forms.returnInAnotherLocation'
              })}
              handleClose={() =>
                setOfferFormImportValue('returnLocationId', null)
              }
            >
              <PlaceInputWrapper>
                <LocationInput
                  required
                  id='returnLocationId'
                  label={intl.formatMessage({
                    id: 'forms.returnTerminalInputLabel'
                  })}
                  locationName='returnLocationId'
                  locationValue={offerFormImport.returnLocationId}
                  locationPlaceholder={intl.formatMessage({
                    id: 'forms.returnTerminalInputLabel'
                  })}
                  onChange={(name, value) =>
                    setOfferFormImportValue(name, value)
                  }
                  placeholder={intl.formatMessage({
                    id: 'forms.locationPlaceholder'
                  })}
                  locationTypes={LOCATION_TYPES.TERMINAL}
                  dictionary={returnDictionary}
                  isLoading={returnDictionary.state === API_STATES.IN_PROGRESS}
                  fetchLocations={fetchLocations}
                  searchHintText={intl.formatMessage({
                    id: 'offersFilters.searchHintText'
                  })}
                />
              </PlaceInputWrapper>
            </CheckboxSection>
            <Padding>
              <FormSectionSpacer>
                {intl.formatMessage({ id: 'forms.cargoSectionName' })}
              </FormSectionSpacer>
              <InputsRowWrapper>
                <DictionaryInputWrapper>
                  <Select
                    required
                    id='containerId'
                    name='containerId'
                    label={intl.formatMessage({ id: 'forms.container' })}
                    value={offerFormImport.containerId}
                    options={dictionaries.containers}
                    onChange={setOfferFormImportValue}
                    placeholder={intl.formatMessage({
                      id: 'forms.searchPlaceholder'
                    })}
                  />
                </DictionaryInputWrapper>
                <WeightInputWrapper>
                  <Select
                    required
                    id='shipownerId'
                    name='shipownerId'
                    label={intl.formatMessage({ id: 'forms.shipowner' })}
                    value={offerFormImport.shipownerId}
                    options={dictionaries.shipowners}
                    onChange={setOfferFormImportValue}
                    placeholder={intl.formatMessage({
                      id: 'forms.searchPlaceholder'
                    })}
                  />
                  {isAnyShipowner && (
                    <FormInputTooltip $interactive>
                      <FormInputTooltipBubble $singleLine>
                        <span role='img' aria-hidden='true'>
                          &#128161;
                        </span>{' '}
                        {intl.formatMessage({
                          id: 'forms.generalCargoExchangeShipownerHelpText'
                        })}{' '}
                        <Link
                          to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.OFFER_FORM_GENERAL}`}
                          onClick={copyToGeneralCargoForm}
                        >
                          {intl.formatMessage({
                            id:
                              'forms.generalCargoExchangeShipownerHelpLinkText'
                          })}
                        </Link>
                        !
                      </FormInputTooltipBubble>
                    </FormInputTooltip>
                  )}
                </WeightInputWrapper>
                <WeightInputWrapper>
                  <TextInput
                    label={intl.formatMessage({ id: 'forms.cargoGrossWeight' })}
                    id='weight'
                    name='weight'
                    placeholder={intl.formatMessage({
                      id: 'forms.cargoGrossWeight'
                    })}
                    value={offerFormImport.weight}
                    onChange={setOfferFormImportValue}
                    validate={validateOfferImport}
                    type='unit'
                    unit='kg'
                  />
                  {!offerFormImport.weight && !isAnyShipowner && (
                    <FormInputTooltip>
                      <FormInputTooltipBubble>
                        <span role='img' aria-hidden='true'>
                          💡
                        </span>{' '}
                        {intl.formatMessage({ id: 'forms.weightHelpText' })}
                      </FormInputTooltipBubble>
                    </FormInputTooltip>
                  )}
                </WeightInputWrapper>
              </InputsRowWrapper>
              <InputsRowWrapper>
                <DictionaryInputWrapper>
                  <Select
                    id='adrClassId'
                    name='adrClassId'
                    label={intl.formatMessage({ id: 'forms.adrClass' })}
                    value={offerFormImport.adrClassId}
                    options={dictionaries.adrClasses}
                    onChange={setOfferFormImportValue}
                    placeholder={intl.formatMessage({
                      id: 'forms.searchPlaceholder'
                    })}
                    width='380px'
                  />
                </DictionaryInputWrapper>
                <DictionaryInputWrapper>
                  <Select
                    id='europeanEmissionStandard'
                    name='europeanEmissionStandard'
                    label={intl.formatMessage({
                      id: 'forms.europeanEmissionStandard'
                    })}
                    value={offerFormImport.europeanEmissionStandard}
                    options={EURO_OPTIONS.map(option => ({
                      ...option,
                      label: intl.formatMessage({ id: option.label })
                    }))}
                    onChange={setOfferFormImportValue}
                    placeholder={intl.formatMessage({
                      id: 'forms.searchPlaceholder'
                    })}
                    width='230px'
                  />
                </DictionaryInputWrapper>
                <CargoTypeInputWrapper>
                  <Select
                    id='cargoTypeId'
                    name='cargoTypeId'
                    label={intl.formatMessage({ id: 'forms.cargoType' })}
                    value={offerFormImport.cargoTypeId}
                    options={dictionaries.cargoTypes}
                    onChange={setOfferFormImportValue}
                    placeholder={intl.formatMessage({
                      id: 'forms.searchPlaceholder'
                    })}
                  />
                </CargoTypeInputWrapper>
              </InputsRowWrapper>
              <FormSubsectionSpacer>
                {intl.formatMessage({ id: 'forms.additionalCargoInformation' })}
              </FormSubsectionSpacer>
              <InputsRowWrapper>
                {!isAuction && (
                  <PriceInputWrapper>
                    <TextInput
                      label={intl.formatMessage({ id: 'forms.price' })}
                      id='price'
                      name='price'
                      placeholder={intl.formatMessage({ id: 'forms.price' })}
                      value={offerFormImport.price}
                      onChange={setOfferFormImportValue}
                      validate={validateOfferImport}
                      type='currency'
                      onCurrencyChange={setOfferFormImportValue}
                      currencyValue={offerFormImport.currency}
                    />
                  </PriceInputWrapper>
                )}
              </InputsRowWrapper>
            </Padding>
            <CheckboxSection
              opened={offerFormImport.customsClearanceLocationIdOpened}
              setOpened={opened =>
                setOfferFormImportValue(
                  'customsClearanceLocationIdOpened',
                  opened
                )
              }
              label={intl.formatMessage({
                id: 'forms.customsClearance'
              })}
              handleClose={() =>
                setOfferFormImportValue('customsClearanceLocationId', null)
              }
            >
              <PlaceInputWrapper>
                <LocationInput
                  required
                  id='customsClearanceLocationId'
                  label={intl.formatMessage({
                    id: 'forms.customsClearanceLocationLabel'
                  })}
                  locationName='customsClearanceLocationId'
                  locationValue={offerFormImport.customsClearanceLocationId}
                  locationPlaceholder={intl.formatMessage({
                    id: 'forms.customsClearanceLocationLabel'
                  })}
                  onChange={(name, value) =>
                    setOfferFormImportValue(name, value)
                  }
                  placeholder={intl.formatMessage({
                    id: 'forms.locationPlaceholder'
                  })}
                  locationTypes={LOCATION_TYPES.CITY}
                  dictionary={destinationDictionary}
                  isLoading={
                    destinationDictionary.state === API_STATES.IN_PROGRESS
                  }
                  fetchLocations={fetchLocations}
                  searchHintText={intl.formatMessage({
                    id: 'offersFilters.searchHintText'
                  })}
                />
              </PlaceInputWrapper>
            </CheckboxSection>
            {!isAuction && (
              <Padding>
                <FormSectionSpacer>
                  {intl.formatMessage({ id: 'forms.additionalInformation' })}
                </FormSectionSpacer>
                <InputsRowWrapper>
                  <InputWrapper>
                    <TextInput
                      label={intl.formatMessage({
                        id: 'forms.additionalInformation'
                      })}
                      id='notes'
                      name='notes'
                      multiline
                      rows={3}
                      placeholder={intl.formatMessage({
                        id: 'forms.additionalInformationPlaceholder'
                      })}
                      value={offerFormImport.notes}
                      onChange={setOfferFormImportValue}
                      validate={validateOfferImport}
                    />
                  </InputWrapper>
                </InputsRowWrapper>
                <FormSectionSpacer />
              </Padding>
            )}
            <CheckboxSectionStyled
              ref={templateRef}
              opened={offerFormImport.templateNameOpened}
              setOpened={opened =>
                setOfferFormImportValue('templateNameOpened', opened)
              }
              label={intl.formatMessage({
                id: 'forms.templateName'
              })}
              handleClose={() => setOfferFormImportValue('templateName', null)}
            >
              <TemplateNameInputWrapper>
                <TextInput
                  required
                  label={intl.formatMessage({
                    id: 'forms.templateNameLabel'
                  })}
                  id='templateName'
                  name='templateName'
                  placeholder={intl.formatMessage({
                    id: 'forms.templateNamePlaceholder'
                  })}
                  value={offerFormImport.templateName}
                  onChange={setOfferFormImportValue}
                  validate={validateOfferImport}
                />
              </TemplateNameInputWrapper>
            </CheckboxSectionStyled>
          </FormInnerWrapper>
          <FormActionBar>
            <Link
              to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_CARGO_IMPORT}`}
            >
              {intl.formatMessage({ id: 'forms.cancel' })}
            </Link>
            <ActionGroup>
              <HideOnMobile>
                <Checkbox
                  name='templateNameOpened'
                  value={offerFormImport.templateNameOpened}
                  option={{
                    value: BOOLEAN.YES,
                    text: intl.formatMessage({
                      id: 'forms.templateName'
                    })
                  }}
                  onChange={name => {
                    setOfferFormImportValue(
                      name,
                      !offerFormImport.templateNameOpened
                    )
                    scrollToRef(templateRef, formContentRef)
                  }}
                />
              </HideOnMobile>
              <CreateOfferMenu
                isLoading={offerFormImport.state === API_STATES.IN_PROGRESS}
                active={
                  hasAvailableVisibilityOptions &&
                  valid &&
                  offerFormImport.state !== API_STATES.IN_PROGRESS
                }
                createOffer={createImportOffer}
                createBulkOffers={createBulkImportOffers}
                createAndDuplicateOffer={createAndDuplicateImportOffer}
                createAndStartNewOffer={createAndStartNewImportOffer}
              >
                {intl.formatMessage({ id: 'forms.addOffer' })}
              </CreateOfferMenu>
            </ActionGroup>
          </FormActionBar>
        </FormSection>
        <Drafts
          fillFormUsingTemplate={fillFormUsingTemplateImport}
          opened={templatesOpened}
          onClose={() => setTemplatesOpened(false)}
          lastOffers={lastOffersImport}
          templates={offerTemplatesImport}
          removeTemplate={removeTemplateImport}
        />
      </Content>
    </Page>
  )
}

const ConnectedComponent = compose(
  connect,
  connectDictionaries
)(OfferFormImport)

const CheckboxSectionStyled = styled(CheckboxSection)`
  ${breakpoint.m`
    margin-bottom: 330px;
  `}
`

export default styled(ConnectedComponent)``
